<script>
    import { to, API, CollectionORM } from "@API";
    import otoFamilyWhite from "@assets/oto_family.png";
    import otoFamilyGray from "@assets/oto_family_gray.png";
    import otoClinicBlack from "@assets/FertilityClinic_Lockup_Black.png";
    import otoClinicLight from "@assets/FertilityClinic_Lockup_Light.png";
    import otoTacticalBlack from "@assets/Tactical_Lockup_black.png";
    import otoTacticalLight from "@assets/Tactical_Lockup_light.png";
    import otoGymBlack from "@assets/Athlete_Lockup_black.png";
    import otoGymLight from "@assets/Athlete_Lockup_light.png";
    import tinycolor from "tinycolor2";
    import Tailwind from "../../TailwindStyles.svelte";
    import Loader from "@components/Loader.svelte";
    import { writable } from "svelte/store";
    import { setContext, onMount } from "svelte";

    import PatientInformation from "./components/PatientInformation.svelte";
    import StripePayment from "@components/StripePayment.svelte";
    import InvoiceInfo from '@components/InvoiceInfo.svelte';
    import TabBarUI from "@components/TabBarUI.svelte";
    import { checkEmailTypo } from "@utils/emailTypoDetector";
    import CompletePurchaseGreeting from "./components/CompletePurchaseGreeting.svelte";
    import PlanPicker from "./components/PlanPicker.svelte";
    import TierPicker from "./components/TierPicker.svelte";
    import {
        TIER_REQUIRED_MESSAGE,
        buildIntervalTabs,
        chargeIntervalFor,
        checkoutPricePayload,
        createPlanState,
        stripeDefaultPriceId,
    } from "./plans";

    const images = [
      '/static/store/store_image_1.webp',
      '/static/store/store_image_2.webp',
      '/static/store/store_image_3.webp',
      '/static/store/store_image_4.webp',
    ];
    const image = images[Math.floor(Math.random() * images.length)];

    export let organization;
    export let product;
    export let user;
    export let location;

    let loading = false;
    let showInvoiceModal = false;
    let invoice;

    organization = JSON.parse(organization);
    product = JSON.parse(product);
    if (location) location = JSON.parse(location);
    user = user && user !== 'null' ? JSON.parse(user) : null;

    /** When true, one-click pay with card from ShopRenewalView (Stripe). */
    let useSavedPaymentMethod = !!(user?.payment_method);

    API.host(organization.api);

    const periodLabels = { year: 'Yearly', month: 'Monthly' };
    const currencies = { 'usd': '$', 'eur': '€', 'gbp': '£', 'cad': 'CAD ', 'aud': 'AUD ' };

    const baseIntervals = buildIntervalTabs(
      product.stripe_price,
      stripeDefaultPriceId(product),
      periodLabels
    );
    let intervals = baseIntervals;

    const InventoryProducts = CollectionORM(API.service('shop/inventory_products', false), [], 'guid', {});
    setContext('Store', { InventoryProducts, organization });

    const BindUserPurchase = API.service("shop/bind_user_purchase/");
    let PurchaseIntentService = API.service("shop/:product_slug/create_payment_intent");
    const Providers = API.service(':type/:guid/service_providers');

    let clicked = false;
    let error, response;
    let error_message = "";
    let formErrors = {};
    let emailTypoWarning = null;
    let elements = {};
    const previewSuccess = ['1', 'true'].includes(new URLSearchParams(window.location.search).get('success'));
    let bindSuccess = previewSuccess;
    let providers = [];
    let providersResponse = [];
    let paymentIntentData;
    let processingStep = false;

    const monthlyIndex = intervals.findIndex(i => i.key === 'month');
    let activeTabIndex = monthlyIndex > -1 ? monthlyIndex : 0;

    $: currentPrice = activePlan?.rawAmount != null
        ? activePlan.rawAmount
        : (intervals[activeTabIndex]?.value || 0);
    $: currency = currencies[activePlan?.currency || intervals[activeTabIndex]?.currency] || '$';

    const orgTypes = { Gym: { providerLabel: 'Coach' }, Health: { providerLabel: 'Doctor' }, Tactical: { providerLabel: 'Instructor' } };
    const providerLabel = orgTypes[organization.type]?.providerLabel || 'Provider';
    const urlParams = new URLSearchParams(window.location.search);
    const providerParam = urlParams.get('dr') || "";
    const couponCodeFromUrl = urlParams.get('coupon') || '';
    const subscriptionGuidFromUrl = urlParams.get('sub') || null;
    const couponConfiguration = product.shop_coupon_configuration
        ? { ...product.shop_coupon_configuration }
        : {
            ...(organization.shop_coupon_configuration || {}),
            ...(location?.shop_coupon_configuration || {}),
        };
    let baseCoupon;
    ({ base_subscription: baseCoupon } = couponConfiguration);
    const {
        plansEnabled,
        licenseTiers,
        showTierPicker,
        selectedTierGuid,
        selectedPlanKey,
        visiblePlans: visiblePlansStore,
        tierObject: tierObjectStore,
        selectedPlan: selectedPlanStore,
        intervals: intervalsStore,
    } = createPlanState(product, couponConfiguration, baseIntervals);

    $: visiblePlans = $visiblePlansStore;
    $: tierObject = $tierObjectStore;
    $: intervals = $intervalsStore;
    $: activePlan = $selectedPlanStore;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    let couponValidating = false;
    let couponValid = false;
    let couponError = null;
    let currentCoupon = null;

    $: couponApplies = currentCoupon && couponValid && isSubscriptionCoupon(currentCoupon) && intervalMatches(currentCoupon);

    function isSubscriptionCoupon(coupon) {
        return coupon.metadata?.type !== 'hardware';
    }

    function selectedBillingInterval() {
        if (plansEnabled && $selectedPlanKey) {
            return $selectedPlanStore?.interval || $selectedPlanKey;
        }
        return intervals[activeTabIndex]?.interval || intervals[activeTabIndex]?.key;
    }

    function intervalMatches(coupon) {
        const selectedInterval = selectedBillingInterval();
        if (!selectedInterval) return true;
        if (coupon.metadata?.month_only) return selectedInterval === 'month';
        if (coupon.metadata?.year_only) return selectedInterval === 'year';
        return true;
    }

    function calculateDiscountedPrice(originalPrice, coupon) {
        if (!coupon || !couponApplies) return originalPrice;
        if (coupon.percent_off) return originalPrice * (1 - coupon.percent_off / 100);
        if (coupon.amount_off) return Math.max(0, originalPrice - coupon.amount_off / 100);
        return originalPrice;
    }

    function getDiscountAmount(originalPrice, coupon) {
        if (!coupon || !couponApplies) return 0;
        return originalPrice - calculateDiscountedPrice(originalPrice, coupon);
    }

    $: discountedPrice = calculateDiscountedPrice(currentPrice, currentCoupon);
    $: discountAmount = getDiscountAmount(currentPrice, currentCoupon);

    $: {
        const billing = selectedBillingInterval();
        if (currentCoupon && currentCoupon.metadata?.month_only && billing === 'year') {
            couponValid = false;
            couponError = 'This coupon is only valid for monthly plans';
        } else if (currentCoupon && currentCoupon.metadata?.month_only && billing === 'month') {
            couponValid = true;
            couponError = null;
        }
    }

    $: payButtonLabel = useSavedPaymentMethod && user?.payment_method ? 'Purchase' : 'Proceed to Payment';

    $: displayAmount =
        (activePlan ? activePlan.interval === 'year' : intervals[activeTabIndex]?.key === 'year')
            ? discountedPrice / 12
            : discountedPrice;

    let formData = {
        is_couple: false,
        firstname: user?.firstname || "",
        lastname: user?.lastname || "",
        email: user?.email || urlParams.get('email') || "",
        service_provider: providerParam === "hide" ? "" : providerParam,
        gender: "F",
        coupon_code: baseCoupon || couponCodeFromUrl || '',
        equipment: [],
    };

    const accentColor = tinycolor(organization.branding_primary_color);
    const overColor = tinycolor(organization.branding_primary_color);
    const overSecondary = tinycolor(organization.branding_secondary_color);
    const overtertiary = tinycolor(organization.branding_tertiary_color);
    const isDark = tinycolor(organization.branding_primary_color).isDark();
    function getFamilyLogo() {
      if (organization.is_fertility_org) {
        return otoClinicBlack;
      }
      if (organization.type.toLowerCase() === 'tactical & first responders') {
        return otoTacticalBlack;
      }
      if (organization.type.toLowerCase() === 'gym') {
        return otoGymBlack;
      }
      return otoFamilyGray;
    }
    let familyLogo = getFamilyLogo();
    const primaryLight = tinycolor.mix(organization.branding_primary_color, "white", 35).toHexString();
    const primaryUltraLight = tinycolor.mix(organization.branding_primary_color, "white", 70).toHexString();
    const primaryDark = tinycolor(organization.branding_primary_color).darken(38).toString();
    const primaryDarker = tinycolor(organization.branding_primary_color).darken(45).toString();
    const secondaryDark = tinycolor(organization.branding_secondary_color).darken(20).toString();
    const tertiaryDark = tinycolor(organization.branding_tertiary_color).darken(38).toString();
    const tertiaryLight = tinycolor.mix(organization.branding_tertiary_color, "white", 35).toHexString();

    let styles = {
        "primary-color": organization.branding_primary_color,
        "primary-light": primaryLight,
        "primary-ultra-light": primaryUltraLight,
        "primary-dark": primaryDark,
        "primary-darker": primaryDarker,
        "secondary-color": organization.branding_secondary_color,
        "secondary-dark": secondaryDark,
        "tertiary-light": tertiaryLight,
        "tertiary-color": organization.branding_tertiary_color,
        "tertiary-dark": tertiaryDark,
        'pnwf-blue': "#232D40",
        "accent-color": accentColor.isLight() ? accentColor.darken(20).toHexString() : accentColor.lighten(20).toHexString(),
        "over-primary-color": overColor.isLight() ? overColor.darken(100).toHexString() : overColor.lighten(100).toHexString(),
        "over-secondary-color": overSecondary.isLight() ? overSecondary.darken(100).toHexString() : overSecondary.lighten(100).toHexString(),
        "over-tertiary-color": overtertiary.isLight() ? overtertiary.darken(100).toHexString() : overtertiary.lighten(100).toHexString(),
    };

    $: cssVarStyles = Object.entries(styles).map(([key, value]) => `--${key}:${value}`).join(";");

    getServiceProviders();

    async function getServiceProviders() {
        const loc = location?.guid || null;
        const type = loc ? 'locations' : 'organizations';
        const args = { is_active: true, type, guid: loc || organization.guid };
        loading = true;
        [error, providersResponse] = await to(Providers.get(args));
        if (!error && providersResponse) {
          const list = providersResponse.map(p => {
            const label = `${p.firstname} ${p.lastname}`;
            if (p.locations?.length > 0) {
              return p.locations.map(l => ({ label: `${p.firstname} ${p.lastname} (${l.title})`, value: p.guid, location: l.guid }));
            }
            return { label, value: p.guid };
          });
          providers = list.flat();
        }
        loading = false;
    }

    async function validateForm() {
        formErrors = {};
        for (const key in elements) {
            if (elements[key]) elements[key].classList.remove("error");
        }

        let validationMap = {
            firstname: true,
            lastname: true,
            email: true,
            gender: true,
            service_provider: providerParam === "hide" || !providers.length ? false : true,
        };

        if (!clicked) return false;

        for (let key of Object.keys(validationMap)) {
            if (!validationMap[key]) continue;
            if (key.includes("email") && !emailRegex.test(formData[key])) {
                formErrors[key] = true;
                elements[key]?.classList.add("error");
            } else if (key.includes("email") && emailRegex.test(formData[key])) {
                const existingWarning = emailTypoWarning;
                if (!(existingWarning && existingWarning.userConfirmed)) {
                    const typoResult = checkEmailTypo(formData[key]);
                    if (typoResult?.typoDetected) {
                        emailTypoWarning = typoResult;
                        formErrors[key] = true;
                    }
                }
            } else if (key.includes("service_provider") && !formData[key]) {
                if (!(location?.guid)) formErrors.service_provider = true;
            } else if (!formData[key]) {
                formErrors[key] = true;
                if (key.includes("gender") || key.includes("email")) elements[key]?.classList.add("error");
            }
        }
        return Object.keys(formErrors).length === 0;
    }

    function acceptSuggestedEmail() {
        if (emailTypoWarning?.suggested) {
            formData.email = emailTypoWarning.suggested;
            emailTypoWarning = { ...emailTypoWarning, typoDetected: false, userConfirmed: true };
        }
    }

    function dismissEmailWarning() {
        if (emailTypoWarning) emailTypoWarning = { ...emailTypoWarning, typoDetected: false, userConfirmed: true };
    }

    function resetEmailWarning() {
        emailTypoWarning = null;
    }

    async function validateCoupon() {
        if (!formData.coupon_code.trim()) {
            couponError = 'Please enter a coupon code';
            return;
        }

        couponValidating = true;
        couponError = null;
        couponValid = false;

        try {
            const res = await fetch(`${organization.api}/shop/${product.slug}/validate_coupon/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    coupon: formData.coupon_code.trim(),
                    product: product.slug,
                }),
            });

            if (res.ok) {
                currentCoupon = await res.json();
                couponValid = true;
                couponError = null;
            } else {
                const errorData = await res.json();
                couponError = errorData.error || 'Invalid coupon code';
                couponValid = false;
                currentCoupon = null;
                formData.coupon_code = '';
            }
        } catch (e) {
            console.error('Coupon validation error:', e);
            couponError = 'Error validating coupon';
            couponValid = false;
            currentCoupon = null;
            formData.coupon_code = '';
        } finally {
            couponValidating = false;
        }
    }

    function removeCoupon() {
        formData.coupon_code = '';
        currentCoupon = null;
        couponValid = false;
        couponError = null;
        if (elements.coupon_code) elements.coupon_code.focus();
    }

    onMount(async () => {
        const initialCoupon = couponCodeFromUrl || baseCoupon;
        if (initialCoupon) {
            formData.coupon_code = initialCoupon;
            await validateCoupon();
        }
    });

    async function handleConfirm() {
        if (emailTypoWarning?.typoDetected) {
            formErrors.email = true;
            elements.email?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        processingStep = true;
        try {
            const isValid = await validateForm();
            if (isValid) await createIntent();
        } finally {
            processingStep = false;
        }
    }

    async function createIntent() {
        error_message = '';
        if (showTierPicker && !$selectedTierGuid) {
            error_message = TIER_REQUIRED_MESSAGE;
            return;
        }
        const selectedInterval = intervals[activeTabIndex]?.key;
        const selectedPlan = $selectedPlanStore;
        const { interval: intervalForCharge, intervalCount: intervalCountForCharge } =
            chargeIntervalFor(intervals, activeTabIndex, selectedPlan, selectedInterval);
        const pricePayload = checkoutPricePayload({
            product,
            tier: tierObject,
            interval: intervalForCharge,
            intervalCount: intervalCountForCharge,
            selectedPlan,
        });

        const couponPayload =
            couponValid && couponApplies && currentCoupon?.id
                ? { base_subscription: currentCoupon.id }
                : {};

        const args = {
            product_slug: product.slug,
            customer_details: formData,
            organization: organization.guid,
            location: location?.guid,
            location_namespace: location?.namespace,
            is_couple: false,
            payment_interval: intervalForCharge,
            interval_count: intervalCountForCharge,
            plan_key: $selectedPlanKey || intervals[activeTabIndex]?.key || selectedInterval,
            ...pricePayload,
            ...(formData.service_provider && { service_provider: formData.service_provider }),
            coupons: couponPayload,
            wants_belt: false,
            is_renewal: true,
            ...(user?.guid && { user_guid: user.guid }),
            ...(useSavedPaymentMethod && user?.payment_method?.id && {
                use_saved_payment_method: true,
                payment_method_id: user.payment_method.id,
            }),
        };

        loading = true;
        [error, response] = await to(PurchaseIntentService.post(args));
        if (response) {
            if (response.already_confirmed) {
                await bindUser({
                    detail: {
                        subscription: response.subscription_id,
                        paymentIntent: response.payment_intent,
                    },
                });
            } else {
                paymentIntentData = {
                    payment_intent: response.payment_intent,
                    client_secret: response.client_secret,
                    subscription_id: response.subscription_id,
                };
                invoice = response.invoice;
                showInvoiceModal = true;
            }
        }
        if (error) {
            error_message = error.data?.error || 'There was an unexpected error. Please contact OTO support at support@oto.coach';
        }
        loading = false;
    }

    async function bindUser({ detail }) {
        loading = true;
        let { subscription, paymentIntent } = detail;

        const data = {
            org: organization.guid,
            subscription: subscription,
            purchase: paymentIntent,
            product: product,
            ...((location || formData.service_provider) && {
                location: location ? location.guid : providers.find(p => p.value === formData.service_provider)?.location
            }),
            ...(tierObject && { tier: tierObject.guid }),
            ...(formData.service_provider && { service_provider: formData.service_provider }),
            generate_jwt: true,
            equipment: [],
            is_renewal: true,
            has_hardware: true,
            ...(user?.guid && { user_guid: user.guid }),
            ...(subscriptionGuidFromUrl && { oto_subscription_guid: subscriptionGuidFromUrl }),
        };

        [error, response] = await to(BindUserPurchase.post(data));
        loading = false;
        if (response) {
            if (response.access_token) {
                localStorage.setItem('token', response.access_token);
                API.setAuthTokenStore(writable(response.access_token));
            }
            bindSuccess = true;
        } else {
            error_message = "There was an error completing your renewal. Please contact OTO support at support@oto.coach";
        }
    }

    function handleTabChange({ detail }) {
        activeTabIndex = detail.index;
    }

    function selectPlan(planKey) {
        $selectedPlanKey = planKey;
        const idx = intervals.findIndex(i => i.key === planKey);
        if (idx >= 0) activeTabIndex = idx;
    }

    function selectTier(tierGuid) {
        if ($selectedTierGuid === tierGuid) return;
        $selectedTierGuid = tierGuid;
        if (paymentIntentData) {
            createIntent();
        }
    }

    function handleCloseInvoiceModal() {
        showInvoiceModal = false;
    }
</script>

<Tailwind />

<div
  class="subscription-invite absolute top-0 left-0 w-full h-full flex items-center justify-center"
  class:dark={isDark}
  class:light={!isDark}
  class:show-errors={Object.keys(formErrors).length}
  style={cssVarStyles}
>
  <div class="org-bg" style="background-color:{organization.branding_secondary_color}" />
  <div class="text-center flex flex-col w-full" class:h-full={!bindSuccess} class:min-h-full={bindSuccess} class:justify-center={!bindSuccess}>
    {#if bindSuccess}
      <CompletePurchaseGreeting {organization} isRenewal={true} />
    {:else}
    <div class="relative flex items-center h-full">
      <div class="main-container h-full overflow-auto lg:flex flex-col items-center justify-start relative lg:mx-auto w-full">

        {#if loading}
        <div class="p-5 fixed inset-0 flex items-center justify-center z-20 bg-black/20">
          <Loader size="50" color="white" />
        </div>
        {/if}

        <div class="logo mt-4 mx-auto mb-6 max-w-lg px-8 pt-4 lg:mt-auto">
          <img class="inline-block rounded-md max-h-28 object-contain" src={organization.branding_logo} alt={organization.nme}>
        </div>

        <form class="bg-white md:rounded-lg p-8 max-w-lg w-full mx-auto" on:submit|preventDefault>
          {#if error_message}
            <div class="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error_message}</div>
          {/if}

          <div class="space-y-6">
            <div>
              <div class="flex items-center gap-2 mb-4">
                <i class="fa-solid fa-rotate text-org-secondary text-xl"></i>
                <h2 class="text-org-secondary-dark text-xl font-bold">Renew Your Subscription</h2>
              </div>
              <p class="text-sm text-gray-600 mb-4">Welcome back! Confirm your details to reactivate your subscription.</p>

              {#if user}
              <div class="space-y-3 text-left">
                <div class="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div class="flex gap-3">
                    <div class="flex-1">
                      <p class="text-xs text-gray-500 mb-1">First Name</p>
                      <p class="text-gray-900 font-medium">{formData.firstname}</p>
                    </div>
                    <div class="flex-1">
                      <p class="text-xs text-gray-500 mb-1">Last Name</p>
                      <p class="text-gray-900 font-medium">{formData.lastname}</p>
                    </div>
                  </div>
                  <div>
                    <p class="text-xs text-gray-500 mb-1">Email</p>
                    <p class="text-gray-900 font-medium">{formData.email}</p>
                  </div>
                  {#if user?.payment_method && useSavedPaymentMethod}
                  <div class="pt-2 border-t border-gray-200">
                    <p class="text-xs text-gray-500 mb-1">Payment</p>
                    <p class="text-gray-900 font-medium capitalize">
                      {user.payment_method.brand} ending in {user.payment_method.last4}
                    </p>
                    <button
                      type="button"
                      class="text-sm text-org-secondary underline mt-1"
                      on:click={() => { useSavedPaymentMethod = false; }}
                    >
                      Use a different card
                    </button>
                  </div>
                  {:else if user?.payment_method}
                  <div class="pt-2 border-t border-gray-200 text-left">
                    <button
                      type="button"
                      class="text-sm text-org-secondary underline"
                      on:click={() => { useSavedPaymentMethod = true; }}
                    >
                      Pay with saved {user.payment_method.brand} ••••{user.payment_method.last4}
                    </button>
                  </div>
                  {/if}
                </div>
              </div>
              {:else}
              <div class="space-y-1">
                <PatientInformation
                  bind:elements={elements}
                  bind:formData={formData}
                  {formErrors}
                  prepopulated={false}
                  {emailTypoWarning}
                  onAcceptSuggestion={acceptSuggestedEmail}
                  onDismissWarning={dismissEmailWarning}
                  onEmailChange={resetEmailWarning}
                />
              </div>
              {/if}
            </div>

            {#if showTierPicker || (plansEnabled && visiblePlans.length > 0)}
            <div class="border-t border-b flex flex-col py-4 gap-4">
              {#if showTierPicker}
              <TierPicker
                tiers={licenseTiers}
                selectedTierGuid={$selectedTierGuid}
                on:select={(e) => selectTier(e.detail)}
              />
              {/if}
              {#if plansEnabled && visiblePlans.length > 0 && (!showTierPicker || $selectedTierGuid)}
              <PlanPicker
                matchedPlans={visiblePlans}
                selectedPlanKey={$selectedPlanKey}
                {currencies}
                {couponApplies}
                {discountAmount}
                {currentCoupon}
                {calculateDiscountedPrice}
                on:select={(e) => selectPlan(e.detail)}
              />
              {/if}
              {#if !plansEnabled && intervals.length > 1 && (!showTierPicker || $selectedTierGuid)}
              <div class="flex items-center justify-between">
                <span class="text-gray-800 text-left">Billing period</span>
                <TabBarUI small pillClass="" tabs={intervals} {activeTabIndex} on:tabchange={handleTabChange} />
              </div>
              {/if}
            </div>
            {:else}
            <div class="border-t border-b flex items-center justify-between py-4">
              <div class="flex flex-col justify-between gap-2 flex-1">
                <span class="text-gray-800 text-left">Subscription Fee</span>
              </div>

              <div class="flex flex-col gap-4 flex-1 items-end">
                {#if intervals.length > 1}
                <div class="text-gray-800 font-medium">
                  <TabBarUI small pillClass="" tabs={intervals} {activeTabIndex} on:tabchange={handleTabChange} />
                </div>
                {/if}
                <div class="text-right">
                  {#if couponApplies && discountAmount > 0}
                  <span class="text-gray-400 line-through text-sm mr-2">{currency}{intervals[activeTabIndex]?.key === 'year' ? (currentPrice / 12).toFixed(2) : currentPrice.toFixed(2)}</span>
                  {/if}
                  <span>{currency}{displayAmount.toFixed(2)} /mth</span>
                </div>
              </div>
            </div>
            {/if}

            <div class="relative pb-2 text-left">
              <div class="relative">
                {#if couponValid}
                  <div class="w-full p-3 pl-12 pr-12 border border-green-500 rounded-full bg-green-50 flex items-center">
                    <div class="absolute left-3 flex items-center">
                      <i class="fa-sharp-duotone fa-regular fa-badge-check text-green-600 text-xl"></i>
                    </div>
                    <span class="text-sm font-medium text-gray-900 flex-1">{formData.coupon_code}</span>
                    <button
                      type="button"
                      on:click={removeCoupon}
                      class="absolute right-3 flex items-center justify-center w-6 h-6 hover:bg-green-200 rounded-full transition-colors"
                      title="Remove coupon"
                    >
                      <i class="fa-sharp fa-light fa-circle-xmark text-green-600 text-lg"></i>
                    </button>
                  </div>
                {:else}
                  <input
                    bind:this={elements.coupon_code}
                    id="coupon-renewal"
                    type="text"
                    bind:value={formData.coupon_code}
                    class="w-full p-3 pr-24 border rounded-full {couponError ? 'border-red-500' : ''}"
                    placeholder="Have a coupon? Enter here"
                    on:input={() => { couponValid = false; couponError = null; }}
                    on:keypress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        validateCoupon();
                      }
                    }}
                  />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3">
                    {#if couponValidating}
                      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-org-primary"></div>
                    {:else}
                      <button
                        type="button"
                        on:click={validateCoupon}
                        disabled={!formData.coupon_code.trim() || couponValidating}
                        class="cursor-pointer px-3 py-1 text-sm bg-org-secondary text-over-secondary rounded-full hover:bg-org-primary-dark disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    {/if}
                  </div>
                {/if}
              </div>
              {#if couponError}
                <p class="bg-red-600 text-white text-xs text-left mt-2 ml-1 py-0.5 px-3 w-fit rounded">{couponError}</p>
              {/if}
            </div>

            <div class="stripe-holder">
              <button
                type="button"
                on:click={() => { clicked = true; handleConfirm(); }}
                disabled={processingStep}
                class="cursor-pointer mt-3 w-full items-center rounded-full border border-transparent bg-org-secondary p-3 font-medium text-over-secondary shadow-sm hover:font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >{#if processingStep}Please wait...{:else}{payButtonLabel}{/if}</button>
            </div>
          </div>
        </form>

        {#if showInvoiceModal}
          <div class="modal-overlay">
            <div class="modal-content" on:click|stopPropagation>
              {#if loading}
              <div class="p-5 fixed inset-0 flex items-center justify-center z-20 bg-black/20">
                <Loader size="50" color="white" />
              </div>
              {/if}

              <div class='w-full flex justify-end flex-col text-left mb-2'>
                <h2 class="text-2xl font-semibold">Complete Your Renewal</h2>
                <button class="close-button" on:click={handleCloseInvoiceModal}><i class="fa-solid fa-x text-base"></i></button>
              </div>

              {#if error_message}
                <div class="bg-red-50 text-red-600 p-4 rounded-lg" style="z-index: 1; background: #ffd6d6; border-radius: 10px; padding: 10px; color: #fc2929;">
                  {error_message}
                </div>
              {/if}

              <div class="md:grid grid-cols-1 gap-3">
                <div>
                  <h3 class="text-left text-xl my-3 mb-2 text-over-primary">Order details:</h3>
                  <InvoiceInfo {invoice} class="rounded-md p-4 bg-gray-50" />

                  <StripePayment intent={paymentIntentData} on:complete={bindUser}>
                    <h3 slot="header" class="text-gray-800 font-medium mt-4 text-xl text-left">Payment Details:</h3>
                    <svelte:fragment slot="button" let:handlePurchase let:purchaseLoading>
                      <button
                        on:click={handlePurchase}
                        disabled={purchaseLoading}
                        class="mt-3 w-full items-center rounded-full border border-transparent bg-org-secondary p-3 font-medium text-over-secondary shadow-sm hover:font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {#if purchaseLoading}Please wait...{:else}Renew Subscription{/if}
                      </button>
                    </svelte:fragment>
                  </StripePayment>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <div class="w-full py-4 mt-auto">
          <img src={familyLogo} alt="" class="w-[245px] mx-auto" />
        </div>
      </div>
    </div>
    {/if}
  </div>
</div>

<style lang="scss" global>

.main-container {
  background: #ebeaea;
}
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    width: 90%;
    max-width: 625px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

    .close-button {
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--tertiary-dark);
      color: white;
      border: none;
      font-size: 1.5rem;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      transition: background-color 0.2s;
    }
    .close-button:hover {
      border: 1px solid white;
    }
  }

  .org-bg {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: -1;
  }

  input, select {
    border-color: rgb(215, 217, 221) !important;
  }

  .stripe-holder {
    max-width: 550px;
    @media screen and (max-width: 900px) {
      margin: 30px auto 0;
    }
  }

  .show-errors {
    input:invalid,
    .error {
      border: red solid 1px;
    }
  }

  .branding-image {
    background-size: cover;
    background-position: center;
  }
</style>
